import React, { useEffect, useState } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Grid,
  Card,
  CardContent,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Alert,
  CircularProgress,
  Backdrop,
  Snackbar,
  Tooltip
} from '@mui/material';
import {
  CalendarToday,
  Edit,
  Settings,
  History,
  Star,
  TrendingUp,
  EmojiEvents,
  Visibility,
  VisibilityOff,
  ContentCopy,
  Key
} from '@mui/icons-material';
import { useAuthenticator } from '@aws-amplify/ui-react';
import { useAuthGen2 } from '../hooks/useAuthGen2';
import { fetchAuthSession } from 'aws-amplify/auth';
import { buildApiEndpoint } from '../configuration/api';

interface GameHistory {
  id: string;
  date: string;
  celebrity: string;
  score: number;
  totalQuestions: number;
  completionTime: number; // in seconds
  hintsUsed: number;
}

interface UserStats {
  totalQuestionsAsked: number;
  totalTriviaViewed: number;
  totalGuesses: number;
  totalGamesPlayed: number;
  gamesCompleted: number;
  totalScore: number;
  averageScore: number;
  currentStreak: number;
  bestStreak: number;
  accountCreated: string;
  lastUpdated: string;
  lastGamePlayed: string;
  completionRate: number;
  averageQuestionsPerGame: number;
  averageGuessesPerGame: number;
}

interface Achievement {
  id: string;
  name: string;
  description: string;
  icon: string;
  category: 'streak' | 'games' | 'performance' | 'special';
  rarity: 'common' | 'rare' | 'epic' | 'legendary';
  earned: boolean;
  earnedAt?: string;
}

const Profile: React.FC = () => {
  const { user } = useAuthenticator((context) => [context.user]);
  const { isAuthenticated, loading: authLoading } = useAuthGen2();
  const [loading, setLoading] = useState(true);
  const [userStats, setUserStats] = useState<UserStats | null>(null);
  const [gameHistory, setGameHistory] = useState<GameHistory[]>([]);
  const [achievements, setAchievements] = useState<Achievement[]>([]);
  const [showFullHistory, setShowFullHistory] = useState(false);
  const [inviteCode, setInviteCode] = useState<string | null>(null);
  const [copySuccess, setCopySuccess] = useState(false);

  // Load user profile when auth is ready
  useEffect(() => {
    // Don't do anything if still loading auth
    if (authLoading) {
      return;
    }
    
    if (isAuthenticated) {
      // TODO: Replace with actual API calls when backend is ready
      loadUserProfile();
    } else {
      setLoading(false);
    }
  }, [authLoading, isAuthenticated]);

  // Load user profile data from API
  const loadUserProfile = async () => {
    try {
      // Get the auth session for the API call
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();

      if (!idToken) {
        throw new Error('No authentication token available');
      }

      // Call the UserStatistics API
      const apiUrl = buildApiEndpoint('/user-statistics', true);
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`API call failed: ${response.status}`);
      }

      const data = await response.json();

      if (data.success && data.statistics) {
        setUserStats(data.statistics);
      } else {
        throw new Error('Invalid response format');
      }

      // Fetch user settings to get invite code
      const settingsUrl = buildApiEndpoint('/user-settings', true);
      const settingsResponse = await fetch(settingsUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (settingsResponse.ok) {
        const settingsData = await settingsResponse.json();
        if (settingsData.success && settingsData.settings?.inviteCode) {
          setInviteCode(settingsData.settings.inviteCode);
        }
      } else {
        console.warn('Failed to load user settings:', settingsResponse.status);
      }

      // Fetch game history
      const historyUrl = buildApiEndpoint('/game-history', true);
      const historyResponse = await fetch(historyUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (historyResponse.ok) {
        const historyData = await historyResponse.json();
        if (historyData.success && historyData.history) {
          setGameHistory(historyData.history);
        }
      } else {
        console.warn('Failed to load game history:', historyResponse.status);
      }

      // Fetch achievements
      const achievementsUrl = buildApiEndpoint('/achievements', true);
      const achievementsResponse = await fetch(achievementsUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (achievementsResponse.ok) {
        const achievementsData = await achievementsResponse.json();
        if (achievementsData.success && achievementsData.achievements) {
          setAchievements(achievementsData.achievements);
        }
      } else {
        console.warn('Failed to load achievements:', achievementsResponse.status);
      }
    } catch (error) {
      console.error('Error loading user profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const handleCopyInviteCode = () => {
    if (inviteCode) {
      navigator.clipboard.writeText(inviteCode);
      setCopySuccess(true);
    }
  };

  // Show loading state
  if (loading || authLoading) {
    return (
      <Backdrop
        sx={(theme) => ({ color: '#fff', zIndex: theme.zIndex.drawer + 1 })}
        open={loading || authLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    );
  }

  // Show auth required message if not authenticated
  if (!isAuthenticated) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, px: { xs: 0.25, sm: 0.2, md: 0.75 } }}>
        <Alert severity="info">
          Please sign in to view your profile and game history.
        </Alert>
      </Container>
    );
  }

  const displayedHistory = showFullHistory ? gameHistory : gameHistory.slice(0, 3);

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4, px: { xs: 0.25, sm: 0.2, md: 0.75 } }}>
      {/* Profile Header */}
      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs>
            <Typography variant="h4" gutterBottom>
              Welcome back!
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <CalendarToday sx={{ mr: 1, color: 'text.secondary' }} />
              <Typography variant="body2" color="text.secondary">
                Member since {userStats?.accountCreated ? new Date(userStats.accountCreated).toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) : 'Recently'}
              </Typography>
            </Box>
            {inviteCode && (
              <Card variant="outlined" sx={{ p: 2, backgroundColor: '#f5f5f5', maxWidth: 350 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                    <Key sx={{ mr: 1, color: 'primary.main' }} />
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Your Invite Code
                      </Typography>
                      <Typography variant="h6" sx={{ fontFamily: 'monospace', letterSpacing: 2 }}>
                        {inviteCode}
                      </Typography>
                    </Box>
                  </Box>
                  <Tooltip title="Copy invite code">
                    <IconButton onClick={handleCopyInviteCode} color="primary" size="small">
                      <ContentCopy />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Share this code with friends so they can add you!
                </Typography>
              </Card>
            )}
          </Grid>
          <Grid item>
            <Button
              variant="outlined"
              startIcon={<Edit />}
              sx={{ mb: 1 }}
            >
              Edit Profile
            </Button>
            <br />
            <Button
              variant="outlined"
              startIcon={<Settings />}
              color="secondary"
              href="/settings"
            >
              Settings
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Stats Cards */}
      {userStats && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <EmojiEvents sx={{ fontSize: 40, color: 'gold', mb: 1 }} />
                <Typography variant="h4" component="div">
                  {userStats.gamesCompleted || 0}
                </Typography>
                <Typography color="text.secondary">
                  Games Won
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <TrendingUp sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                <Typography variant="h4" component="div">
                  {userStats.averageScore?.toFixed(1) || '0.0'}
                </Typography>
                <Typography color="text.secondary">
                  Average Score
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Star sx={{ fontSize: 40, color: 'orange', mb: 1 }} />
                <Typography variant="h4" component="div">
                  {userStats.currentStreak || 0}
                </Typography>
                <Typography color="text.secondary">
                  Current Streak
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <History sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
                <Typography variant="h4" component="div">
                  {userStats.totalGamesPlayed || 0}
                </Typography>
                <Typography color="text.secondary">
                  Games Played
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Detailed Stats */}
      {userStats && (
        <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h5" gutterBottom>
            Detailed Statistics
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <List>
                <ListItem>
                  <ListItemIcon>
                    <Star />
                  </ListItemIcon>
                  <ListItemText
                    primary="Total Questions Asked"
                    secondary={userStats.totalQuestionsAsked || 0}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <EmojiEvents />
                  </ListItemIcon>
                  <ListItemText
                    primary="Best Streak"
                    secondary={`${userStats.bestStreak || 0} games`}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <TrendingUp />
                  </ListItemIcon>
                  <ListItemText
                    primary="Completion Rate"
                    secondary={`${userStats.completionRate || 0}%`}
                  />
                </ListItem>
              </List>
            </Grid>
            <Grid item xs={12} sm={6}>
              <List>
                <ListItem>
                  <ListItemIcon>
                    <History />
                  </ListItemIcon>
                  <ListItemText
                    primary="Total Guesses Made"
                    secondary={userStats.totalGuesses || 0}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CalendarToday />
                  </ListItemIcon>
                  <ListItemText
                    primary="Average Questions Per Game"
                    secondary={userStats.averageQuestionsPerGame?.toFixed(1) || '0.0'}
                  />
                </ListItem>
              </List>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Achievements */}
      {achievements.length > 0 && (
        <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h5" gutterBottom>
            Achievements
          </Typography>
          <Grid container spacing={2}>
            {achievements.map((achievement) => (
              <Grid item xs={6} sm={4} md={3} key={achievement.id}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    p: 2,
                    opacity: achievement.earned ? 1 : 0.4,
                    position: 'relative',
                    backgroundColor: achievement.earned
                      ? achievement.rarity === 'legendary'
                        ? '#fff3e0'
                        : achievement.rarity === 'epic'
                        ? '#f3e5f5'
                        : achievement.rarity === 'rare'
                        ? '#e3f2fd'
                        : 'white'
                      : '#f5f5f5',
                    border: achievement.earned ? '2px solid' : '1px solid #ddd',
                    borderColor: achievement.earned
                      ? achievement.rarity === 'legendary'
                        ? '#ff9800'
                        : achievement.rarity === 'epic'
                        ? '#9c27b0'
                        : achievement.rarity === 'rare'
                        ? '#2196f3'
                        : '#4caf50'
                      : '#ddd'
                  }}
                >
                  <Box sx={{ fontSize: '3rem', mb: 1 }}>
                    {achievement.icon}
                  </Box>
                  <Typography variant="subtitle2" align="center" gutterBottom fontWeight="bold">
                    {achievement.name}
                  </Typography>
                  <Typography variant="caption" align="center" color="text.secondary">
                    {achievement.description}
                  </Typography>
                  {achievement.earned && achievement.earnedAt && (
                    <Chip
                      label={new Date(achievement.earnedAt).toLocaleDateString()}
                      size="small"
                      sx={{ mt: 1 }}
                      color="primary"
                    />
                  )}
                  {!achievement.earned && (
                    <Chip
                      label="Locked"
                      size="small"
                      sx={{ mt: 1 }}
                      color="default"
                    />
                  )}
                </Card>
              </Grid>
            ))}
          </Grid>
        </Paper>
      )}

      {/* Game History */}
      <Paper elevation={3} sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h5">
            Game History
          </Typography>
          <IconButton
            onClick={() => setShowFullHistory(!showFullHistory)}
            aria-label="toggle history view"
          >
            {showFullHistory ? <VisibilityOff /> : <Visibility />}
          </IconButton>
        </Box>
        
        {gameHistory.length === 0 ? (
          <Alert severity="info">
            No games played yet. Start playing to see your history here!
          </Alert>
        ) : (
          <>
            {displayedHistory.map((game, index) => (
              <Box key={game.id}>
                <Card variant="outlined" sx={{ mb: 2 }}>
                  <CardContent>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} sm={4}>
                        <Typography variant="h6">
                          {game.celebrity}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {formatDate(game.date)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={2}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="h5" color="primary">
                            {game.score}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Score
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={2}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="body2">
                            {formatTime(game.completionTime)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Time
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={2}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="body2">
                            {game.hintsUsed}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Hints Used
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={2}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="body2" color="text.secondary">
                            {game.score}/{game.totalQuestions * 20}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Max Score
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
                {index < displayedHistory.length - 1 && <Divider />}
              </Box>
            ))}
            
            {!showFullHistory && gameHistory.length > 3 && (
              <Button
                fullWidth
                variant="outlined"
                onClick={() => setShowFullHistory(true)}
                sx={{ mt: 2 }}
              >
                Show All {gameHistory.length} Games
              </Button>
            )}
            
            {showFullHistory && (
              <Button
                fullWidth
                variant="outlined"
                onClick={() => setShowFullHistory(false)}
                sx={{ mt: 2 }}
              >
                Show Less
              </Button>
            )}
          </>
        )}
      </Paper>

      {/* Copy Success Snackbar */}
      <Snackbar
        open={copySuccess}
        autoHideDuration={3000}
        onClose={() => setCopySuccess(false)}
        message="Invite code copied to clipboard!"
      />
    </Container>
  );
};

export default Profile;